import { logger } from "@/lib/logger";
/**
 * Offline Help Dialog
 * Shows detailed queue status and help information
 * 
 * CRASH-SAFE: All operations wrapped in try-catch
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { 
  HelpCircle, 
  Cloud, 
  CloudOff, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  ExternalLink,
  WifiOff
} from 'lucide-react';
import { getOfflineQueueCount, processOfflineQueue } from '@/utils/offlineEdgeFunctions';
import { offlineFileQueue } from '@/utils/offlineFileQueue';
import { offlineQueue } from '@/lib/offlineQueue';

interface OfflineHelpDialogProps {
  isOnline: boolean;
  triggerClassName?: string;
}

export function OfflineHelpDialog({ isOnline, triggerClassName }: OfflineHelpDialogProps) {
  const [open, setOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [queueCounts, setQueueCounts] = useState({
    edgeFunctions: 0,
    files: 0,
    apiRequests: 0
  });

  const updateQueueCounts = useCallback(async () => {
    try {
      const edgeFunctions = getOfflineQueueCount();
      
      let files = 0;
      try {
        files = await offlineFileQueue.getQueueCount();
      } catch {
        files = 0;
      }
      
      let apiRequests = 0;
      try {
        apiRequests = offlineQueue.getCount();
      } catch {
        apiRequests = 0;
      }
      
      setQueueCounts({ edgeFunctions, files, apiRequests });
    } catch (error) {
      logger.warn('[OfflineHelpDialog] updateQueueCounts error:', error);
      setQueueCounts({ edgeFunctions: 0, files: 0, apiRequests: 0 });
    }
  }, []);

  useEffect(() => {
    if (open) {
      updateQueueCounts();
    }
  }, [open, updateQueueCounts]);

  const totalPending = queueCounts.edgeFunctions + queueCounts.files + queueCounts.apiRequests;

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      await processOfflineQueue();
      
      try {
        offlineQueue.retry();
      } catch (error) {
        logger.warn('[OfflineHelpDialog] offlineQueue.retry error:', error);
      }
      
      await updateQueueCounts();
    } catch (error) {
      logger.warn('[OfflineHelpDialog] handleRetry error:', error);
    } finally {
      setIsRetrying(false);
    }
  }, [updateQueueCounts]);

  const getStatusInfo = useCallback(() => {
    if (!isOnline) {
      return {
        icon: <WifiOff className="h-8 w-8 text-destructive" />,
        title: "You're Offline",
        description: "No internet connection detected. Don't worry - your work is being saved locally!",
        color: "destructive" as const
      };
    }
    if (totalPending > 0) {
      return {
        icon: <RefreshCw className="h-8 w-8 text-warning" />,
        title: "Syncing Pending",
        description: `You have ${totalPending} item${totalPending > 1 ? 's' : ''} waiting to sync. They'll upload automatically.`,
        color: "warning" as const
      };
    }
    return {
      icon: <Cloud className="h-8 w-8 text-success" />,
      title: "All Synced",
      description: "Your data is safely stored in the cloud. You're all set!",
      color: "success" as const
    };
  }, [isOnline, totalPending]);

  const status = getStatusInfo();

  const handleNavigateToManual = useCallback(() => {
    try {
      setOpen(false);
      // Use window.location for router-safe navigation (works outside Router context)
      window.location.href = '/user-manual?tab=offline';
    } catch (error) {
      logger.warn('[OfflineHelpDialog] Navigation error:', error);
    }
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={triggerClassName}
          aria-label="Offline help"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-xl">
        <SheetHeader className="text-center pb-4">
          <div className="mx-auto mb-2">{status.icon}</div>
          <SheetTitle>{status.title}</SheetTitle>
          <SheetDescription>{status.description}</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 overflow-y-auto max-h-[calc(85vh-180px)] pb-4">
          {/* Current Status Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Queue Status</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">{queueCounts.edgeFunctions}</p>
                <p className="text-xs text-muted-foreground">Functions</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">{queueCounts.files}</p>
                <p className="text-xs text-muted-foreground">Files</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">{queueCounts.apiRequests}</p>
                <p className="text-xs text-muted-foreground">Requests</p>
              </div>
            </div>
          </div>

          {/* What You Can Do Now */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">What You Can Do Right Now</h3>
            {!isOnline ? (
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                  <p className="text-sm">Continue filling forms - they save automatically</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                  <p className="text-sm">Upload documents - they'll queue for later</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                  <p className="text-sm">View any data you've already loaded</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-destructive/10 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm">Don't close browser - your data needs to sync first</p>
                </div>
              </div>
            ) : totalPending > 0 ? (
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                  <p className="text-sm">Keep working - sync happens in background</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <p className="text-sm">Wait for sync before ending your shift</p>
                </div>
                <Button 
                  onClick={handleRetry} 
                  disabled={isRetrying}
                  className="w-full"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Syncing...' : 'Retry Sync Now'}
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
                <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                <p className="text-sm">All your data is synced! You can safely close the browser.</p>
              </div>
            )}
          </div>

          {/* Quick Tips */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Quick Tips</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 border rounded-lg">
                <Cloud className="h-5 w-5 text-success mx-auto mb-1" />
                <p className="text-xs font-medium">Green</p>
                <p className="text-xs text-muted-foreground">Synced</p>
              </div>
              <div className="p-2 border rounded-lg">
                <RefreshCw className="h-5 w-5 text-warning mx-auto mb-1" />
                <p className="text-xs font-medium">Orange</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="p-2 border rounded-lg">
                <CloudOff className="h-5 w-5 text-destructive mx-auto mb-1" />
                <p className="text-xs font-medium">Red</p>
                <p className="text-xs text-muted-foreground">Offline</p>
              </div>
            </div>
          </div>

          {/* Learn More Link */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleNavigateToManual}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View Full Documentation
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}